import AppLayout from '../../layouts/AppLayout';
import { Switch } from "../../components/switch";
import { useState, useEffect } from "react"; 
import { Setting } from '../../app-schemas/community/community.interface';
import { useSettings } from '../../api-services/settings/use-setting';
import { useUpdateSettings } from '../../api-services/settings/use-update-settings';
import { pageLoader } from '../../app-utils';
import { FiLogOut, FiTrash2, FiChevronDown } from 'react-icons/fi';
import { useTheme } from '../../context/theme-context';

const Settings = () => {
  document.body.classList.remove('overflow-hidden');
  
  // Get theme context
  const { isDarkMode, setDarkMode } = useTheme();

  const [settings, setSettings] = useState<Setting>({
    account_name: false,
    phone_details: false,
    live_notification: false,
    group_messages: false,
    preferred_lang: "English (Device Language)",
    autoplay_media: false,
    autodownload_media: false,
    dark_mode: isDarkMode
  });
  
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);

  const updateSettings = (name: keyof Setting, value: boolean | string) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [name]: value
    }));

    if (name === 'dark_mode' && typeof value === 'boolean') {
      setDarkMode(value);
    }
  };

  const userSettings = useSettings();
  const updateUserSettings = useUpdateSettings();

  const onSubmit = async (data: any) => {
    updateUserSettings.mutate(data);
  };

  useEffect(() => {
    if (updateUserSettings.isSuccess) {
      pageLoader();
    }
   // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateUserSettings.isSuccess]);

  useEffect(() => {
    if (userSettings.isSuccess) {
      Object.keys(userSettings.data.data).forEach(key => {
        const settingKey = key as keyof Setting;
        const value = userSettings.data.data[settingKey];
        updateSettings(settingKey, value);
        
        if (settingKey === 'dark_mode' && typeof value === 'boolean') {
          setDarkMode(value);
        }
      });
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettings.isSuccess]);

  const handleSwitchChange = (name: keyof Setting, checked: boolean) => {
    updateSettings(name, checked);
  };

  const handleLanguageSelect = (lang: string) => {
    updateSettings('preferred_lang', lang);
    setIsLangDropdownOpen(false);
  };

  const updateSetting = () => {
    pageLoader(true);
    onSubmit(settings);
  };

  const handleLogout = () => {
    console.log('Logout clicked');
  };

  const handleDeleteAccount = () => {
    console.log('Delete account clicked');
  };

  const languageOptions = [
    "English (Device Language)",
    "Spanish", "French", "German", "Italian", 
    "Portuguese", "Dutch", "Russian", "Japanese", 
    "Chinese", "Arabic", "Hindi"
  ];

  return (
    <AppLayout title="Catapot - Settings">
      <section className="w-full mb-0 md:mt-0 md:mb-0 pb-8 bg-white dark:bg-neutral-800">
        <div className="min-h-screen py-5 rounded-md flex flex-col gap-4 w-full lg:gap-3 lg:w-[55%] mx-auto box-border text-gray-900 dark:text-gray-100">
          <h1 className="madimi-one font-bold px-5 text-xl text-gray-900 dark:text-white">
            Privacy and Settings
          </h1>
          
          <div className="min-h-screen py-5 flex flex-col gap-1 lg:gap-2 w-full lg:w-full mx-auto box-border px-5 bg-white dark:bg-neutral-800">
            
            {/* Account Preference Section */}
            <h1 className="madimi-one font-semibold text-lg text-gray-900 dark:text-white">
              Account Preference
            </h1>
            <hr className="-mx-5 mb-2 border-gray-200 dark:border-gray-700" />
            
            <div className="flex flex-col py-2">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="font-bold text-sm text-gray-900 dark:text-gray-200">
                    Account Name
                  </h2>
                  <p className="text-[#C2B9B9] dark:text-gray-400 text-xs">
                    {settings?.account_name ? 'Visible' : 'Not visible'}
                  </p>
                </div>
                <Switch
                  checked={settings.account_name}
                  onCheckedChange={(checked: any) => handleSwitchChange('account_name', checked)}
                />
              </div>
            </div>
            
            <div className="flex flex-col py-2">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="font-bold text-sm text-gray-900 dark:text-gray-200">
                    Phone Details
                  </h2>
                  <p className="text-[#C2B9B9] dark:text-gray-400 text-xs">
                    {settings?.phone_details ? 'Visible' : 'Not visible'}
                  </p>
                </div>
                <Switch 
                  checked={settings.phone_details}
                  onCheckedChange={(checked: any) => handleSwitchChange('phone_details', checked)}
                />
              </div>
            </div>
            
            <div className="flex flex-col py-2">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="font-bold text-sm text-gray-900 dark:text-gray-200">
                    Live Notification Tab
                  </h2>
                  <p className="text-[#C2B9B9] dark:text-gray-400 text-xs">
                    {settings.live_notification ? 'Visible' : 'Not visible'}
                  </p>
                </div>
                <Switch 
                  checked={settings.live_notification}
                  onCheckedChange={(checked: any) => handleSwitchChange('live_notification', checked)} 
                />
              </div>
            </div>
            
            <div className="flex flex-col py-2">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="font-bold text-sm text-gray-900 dark:text-gray-200">
                    Group Messages
                  </h2>
                  <p className="text-[#C2B9B9] dark:text-gray-400 text-xs">
                    {settings?.group_messages ? 'Visible' : 'Not visible'}
                  </p>
                </div>
                <Switch 
                  checked={settings.group_messages}
                  onCheckedChange={(checked: any) => handleSwitchChange('group_messages', checked)} 
                />
              </div>
            </div>
            
            {/* General Preference Section */}
            <h1 className="madimi-one mt-6 text-lg text-gray-900 dark:text-white">
              General Preference
            </h1>
            <hr className="-mx-5 mb-2 border-gray-200 dark:border-gray-700" />
            
            <div className="flex flex-col py-2">
              <div className="flex flex-col gap-2">
                <div>
                  <h2 className="font-bold text-sm text-gray-900 dark:text-gray-200">
                    Preferred Language
                  </h2>
                  <p className="text-[#C2B9B9] dark:text-gray-400 text-xs">
                    {settings?.preferred_lang}
                  </p>
                </div>
                
                {/* Custom Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                    className={`flex items-center gap-2 text-xs border rounded-full px-3 py-2.5 min-w-[160px] justify-between transition-colors w-3/4
                      bg-gray-50 border-gray-200 hover:bg-gray-100
                      dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-600
                    `}
                  >
                    <span className="truncate">{settings?.preferred_lang}</span>
                    <FiChevronDown className={`transition-transform ${isLangDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {isLangDropdownOpen && (
                    <>
                      <div 
                        className="fixed inset-0 z-10"
                        onClick={() => setIsLangDropdownOpen(false)}
                      />
                      <div className="absolute right-0 mt-1 w-full border rounded-lg shadow-lg z-20 max-h-60 overflow-y-auto
                        bg-white border-gray-200
                        dark:bg-gray-800 dark:border-gray-700"
                      >
                        {languageOptions.map((lang) => (
                          <button
                            key={lang}
                            onClick={() => handleLanguageSelect(lang)}
                            className={`w-full text-left px-3 py-2 text-xs transition-colors
                              hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-200
                              ${settings?.preferred_lang === lang 
                                ? 'bg-blue-50 text-blue-600 font-medium dark:bg-gray-700 dark:text-blue-400' 
                                : ''
                              }
                            `}
                          >
                            {lang}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex flex-col py-2">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="font-bold text-sm text-gray-900 dark:text-gray-200">
                    Auto-play audio
                  </h2>
                  <p className="text-[#C2B9B9] dark:text-gray-400 text-xs">
                    {settings?.autoplay_media ? 'On' : 'Off'}
                  </p>
                </div>
                <Switch 
                  checked={settings?.autoplay_media}
                  onCheckedChange={(checked: any) => handleSwitchChange('autoplay_media', checked)}
                />
              </div>
            </div>
            
            <div className="flex flex-col py-2">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="font-bold text-sm text-gray-900 dark:text-gray-200">
                    Auto Download media
                  </h2>
                  <p className="text-[#C2B9B9] dark:text-gray-400 text-xs">
                    {settings?.autodownload_media ? 'On' : 'Off'}
                  </p>
                </div>
                <Switch 
                  checked={settings?.autodownload_media}
                  onCheckedChange={(checked: any) => handleSwitchChange('autodownload_media', checked)} 
                />
              </div>
            </div>
            
            {/* Dark Mode Toggle */}
            <div className="flex flex-col py-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div>
                    <h2 className="font-bold text-sm text-gray-900 dark:text-gray-200">
                      Dark Mode
                    </h2>
                    <p className="text-[#C2B9B9] dark:text-gray-400 text-xs">
                      {settings?.dark_mode ? 'On - Dark theme active' : 'Off - Light theme active'}
                    </p>
                  </div>
                </div>
                <Switch 
                  checked={settings?.dark_mode}
                  onCheckedChange={(checked: any) => handleSwitchChange('dark_mode', checked)} 
                />
              </div>
            </div>
            
            {/* Account Actions Section */}
            <h1 className="madimi-one mt-6 text-lg text-gray-900 dark:text-white">
              Account
            </h1>
            <hr className="-mx-5 border-gray-200 dark:border-gray-700" />
            
            <div className="flex flex-col py-2">
              <button 
                onClick={handleLogout}
                className="flex justify-between items-center w-full py-3 px-2 rounded-lg transition-colors
                  hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-200"
              >
                <span className="font-bold text-sm">Logout</span>
                <FiLogOut className="text-red-500 text-lg" />
              </button>
            </div>
            
            <div className="flex flex-col">
              <button 
                onClick={handleDeleteAccount}
                className="flex justify-between items-center w-full py-3 px-2 rounded-lg transition-colors
                  hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-200"
              >
                <span className="font-bold text-sm">Delete Account</span>
                <FiTrash2 className="text-red-500 text-lg" />
              </button>
            </div>
            
            {/* Save Button */}
            <div className="flex flex-col items-center justify-center pt-6 pb-2 mt-2">
              <button 
                onClick={() => updateSetting()} 
                className="h-[51px] justify-center w-full lg:w-[162px] rounded-lg bg-custom-600 font-bold text-white text-xs hover:bg-custom-700 transition-colors"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      </section>
    </AppLayout>
  );
};

export default Settings;